// React
import React, { useState, useEffect } from "react";

// Libraries
import PropTypes from "prop-types";
import { Icon, Label } from "semantic-ui-react";
import { isEmpty, get, includes, find } from "lodash";
import moment from "moment";

// Redux
import { connect } from "react-redux";

// Hooks
import useAlert from "@Hooks/useAlert";
import usePanelVisibility from "@Hooks/usePanelVisibility";

// Components
import Text from "@Components/Text";
import PlatformIcon from "@Components/PlatformIcon";

// Utils
import { getTimeLabel } from "@Utils/functionality";
import { truncateText } from "@Utils";
import { formatPhone } from "@Utils/formats";

// Styles
import styles from "./styles.module.css";

const ChatCard = ({ alerts, chat, isHistoric = false, isSelected = false, style, onPress, dateField = "closeDate" }) => {
	const { isMobile } = usePanelVisibility();
	const [icon, setIcon] = useState("");
	const [lastMessage, setLastMessage] = useState("");
	const alert = useAlert(chat, alerts, isHistoric);
	const platform = get(chat, "channel.platform.name", "");
	const isWhatsapp = includes(["Whatsapp", "Whatsapp360", "Personal"], platform);
	const isWhatsappPure = includes(["Whatsapp"], platform);
	const origin = get(chat, "origin", "");
	const hasTouch = isMobile || !isSelected;

	useEffect(() => {
		if (!isEmpty(chat)) {
			let itemType = get(chat, "message.type", "");
			let itemText = truncateText(get(chat, "message.text", ""), 30);
			if (platform === "Email") {
				const message = find(chat.messages, { type: "Email" });
				itemType = message?.type;
				itemText = message?.text;
			}
			let messageText = itemText;
			let tmpIcon = "";
			switch (itemType) {
				case "Audio":
					tmpIcon = "headphones";
					messageText = "Audio";
					break;
				case "Video":
					tmpIcon = "video";
					messageText = "Video";
					break;
				case "Location":
					tmpIcon = "map marker alternate";
					messageText = "Ubicación";
					break;
				case "Document":
					tmpIcon = "file alternate outline";
					messageText = "Documento";
					break;
				case "Contact":
					tmpIcon = "users";
					messageText = "Contacto";
					break;
				case "Image":
					tmpIcon = "image";
					messageText = "Imagen";
					break;
				case "Note":
					tmpIcon = "sticky note outline";
					messageText = itemText || "Nota";
					break;
				case "Call":
					tmpIcon = "phone";
					messageText = "Llamada";
					break;
				case "Text":
				case "Email":
					messageText = itemText;
					break;
				default:
					break;
			}
			setLastMessage(messageText);
			setIcon(tmpIcon);
		}
	}, [chat?.id, chat?.message?.id]);

	const getClosedState = () => {
		if (get(chat, "closeTab", false)) {
			return "Cliente";
		}
		return "Atender";
	};

	const renderState = (state) => {
		if (!isEmpty(state)) {
			let className = "";

			switch (state) {
				case "Nuevo":
					className = styles.nuevo;
					break;
				case "Atender":
					className = styles.atender;
					break;
				case "Cliente":
					className = styles.cliente;
					break;
				default:
					break;
			}

			return <Label className={`${styles.tag} ${className}`}>{state}</Label>;
		}
		return null;
	};

	const renderAlert = () => {
		if (!isEmpty(alert)) {
			let extraClass = "";

			switch (alert.name) {
				case "Vencido":
					extraClass = styles.alertTagVencida;
					break;
				case "En Atraso":
					extraClass = styles.alertTagAtraso;
					break;
				case "A Tiempo":
					extraClass = styles.alertTagATiempo;
					break;
				default:
					break;
			}

			return <Label className={`${styles.tag} ${extraClass}`}>{alert.name}</Label>;
		}

		return null;
	};

	const renderTime = () => {
		if (isHistoric) {
			const timestamp = dateField === "closeDate" ? get(chat, "closeDate", null) : get(chat, "createdDate", null);
			if (!timestamp) return "";

			const momentTime = moment(timestamp);
			return getTimeLabel(momentTime, "DD/MM/YYYY");
		}

		const timestamp = get(chat, "message.timestamp", null);
		if (!timestamp) return "";

		const momentTime = moment(timestamp);
		const diff = moment().diff(momentTime, "day");

		if (diff === 0) {
			return getTimeLabel(momentTime, "h:mm A");
		}

		return `${diff} días`;
	};

	const renderLastMessageTime = () => {
		if (!isHistoric) return null;
		const timestamp = get(chat, "message.timestamp", null);
		if (!timestamp) return null;

		const momentTime = moment(timestamp);
		return getTimeLabel(momentTime, "h:mm A");
	};

	const getPrintText = () => {
		if (isWhatsapp) {
			return formatPhone(get(chat, "contact.phone", ""));
		}
		if (platform === "Email") {
			const message = find(chat.messages, { type: "Email" });
			return truncateText(get(message, "text", ""), 30);
		}
		if (get(chat, "message.type") === "Text") {
			return truncateText(get(chat, "message.text", ""), 30);
		}
		return get(chat, "message.type");
	};

	return (
		<div
			style={style}
			className={isSelected ? styles.selectedContainer : styles.container}
			role="button"
			tabIndex={-1}
			onClick={hasTouch ? onPress : undefined}>
			<div className={styles.rowContainer}>
				<PlatformIcon name={platform} />
				<div className={styles.columnContainer}>
					<div className={styles.rowContainer}>
						<Text className={styles.contactText}>{`${get(chat, "contact.name", "")}${
							platform === "Email" ? ` - ${get(chat, "id", "")}` : ""
						}`}</Text>
						{renderState(isHistoric ? getClosedState() : chat.state)}
					</div>
					<div className={styles.rowContainer}>
						{!isEmpty(icon) && <Icon className={styles.icon} name={icon} />}
						<Text className={styles.msgText} withMarkdown>
							{lastMessage}
						</Text>
						<Text className={styles.msgTextPrint}>{getPrintText()}</Text>
						<Text className={styles.timeText}>{`${isWhatsappPure && !isEmpty(origin) ? `${origin} / ` : ""}${renderTime()}`}</Text>
						{renderLastMessageTime() && <Text className={styles.timeText}>{renderLastMessageTime()}</Text>}
					</div>
				</div>
			</div>
			<div className={styles.rowContainerStates}>
				<div className={styles.stateContainerLeft}>
					{!isEmpty(chat.group) && (
						<Label className={styles.tag} color="blue">
							{get(chat, "group.name", "")}
						</Label>
					)}
				</div>
				<div className={styles.stateContainer}>
					{!isEmpty(chat.tags) && (
						<Label className={styles.tag} style={{ backgroundColor: chat.tags[0].color, color: "white" }}>
							{get(chat.tags[0], "name", "")}
						</Label>
					)}
				</div>
				<div className={styles.stateContainerRight}>{renderAlert()}</div>
			</div>
			<div className={styles.rowContainerStates}>
				<div className={styles.infoContainerLeft}>
					<Text className={styles.agentText}>{`Atiende: ${get(chat, "user.fullName", "")}`}</Text>
				</div>
				<div className={styles.infoContainer}>
					{!isEmpty(chat.action) && (
						<Label className={styles.tag} color="black">
							{chat.action}
						</Label>
					)}
				</div>
				<div className={styles.infoContainerRight}>
					{!isHistoric && chat.count !== 0 && (
						<div className={styles.badgeContainer}>
							<div className={styles.badge}>
								<Text className={styles.badgeText}>{chat.count}</Text>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

ChatCard.propTypes = {
	alerts: PropTypes.array,
	chat: PropTypes.object,
	isHistoric: PropTypes.bool,
	isSelected: PropTypes.bool,
	style: PropTypes.object,
	onPress: PropTypes.func,
	dateField: PropTypes.string,
};

function mapStateToProps({ company }) {
	return { alerts: company.settings.alerts };
}

export default connect(mapStateToProps)(ChatCard);
